import { Money } from "@commercetools/platform-sdk";
import cx from "clsx";
import { useTranslations } from "next-intl";
import React, { useCallback } from "react";

import { Link } from "@/common/components/link";
import { formatDateCompact } from "@/common/hooks/use-date";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { ROUTES } from "@/features/store/settings/routes";
import { formatPrice } from "../hooks/use-price";
import { useProductDispatchInfo } from "./hooks/use-product-dispatch-info";

export const ProductProductionUpgrade = () => {
  const t = useTranslations("common");
  const { pricingResult, deliveryOption, setDeliveryOption } = useStoreProduct();

  const { orderCompletionTime } = useProductDispatchInfo({
    cutOffDate: deliveryOption?.cutOffDate,
  });

  const formatDeliveryPrice = useCallback(
    (price: Money, id?: string) => {
      if (price.centAmount === 0) return t("text-free");
      let targetPrice = price.centAmount;
      if (deliveryOption?.deliveryRuleId !== id) {
        targetPrice = price.centAmount - (deliveryOption?.price.centAmount ?? 0);
      }

      return `${targetPrice > 0 ? "+ " : "- "}${formatPrice({ ...price, centAmount: Math.abs(targetPrice) })}`;
    },
    [deliveryOption?.price.centAmount, t, deliveryOption?.deliveryRuleId],
  );

  if (!pricingResult?.deliveryOptions || pricingResult.deliveryOptions.length <= 1) {
    return;
  }

  pricingResult?.deliveryOptions?.sort(
    (deliveryOptionA, deliveryOptionB) =>
      deliveryOptionB.price?.centAmount - deliveryOptionA.price?.centAmount,
  );

  return (
    <div id="ProductProductionUpgrade" className="mt-6">
      <h3 className="text-grey-90 text-center text-xl font-semibold md:text-2xl">
        {t("text-product-production-upgrade")}
      </h3>
      <div className="my-6 flex flex-row flex-wrap justify-between gap-5 xl:flex-nowrap">
        {pricingResult?.deliveryOptions?.map((option) => (
          <div
            key={option.deliveryRuleId}
            className={cx(
              "basis-[46%] border-grey-50 rounded-lg border p-5 cursor-pointer hover:bg-grey-10 select-none",
              {
                "bg-berry-10 outline-berry-90 outline border-transparent":
                  (!deliveryOption?.deliveryRuleId && !option.deliveryRuleId) ||
                  option.deliveryRuleId === deliveryOption?.deliveryRuleId,
              },
            )}
            onClick={() => setDeliveryOption(option)}
          >
            <p className="m-0 text-center text-2xl font-semibold">{option.name}</p>
            <p className="m-0 text-center">
              {t("text-for-delivery-on")}:{" "}
              <span className="font-semibold">{formatDateCompact(option.deliveryDate)}*</span>
            </p>
            <p className="mt-4 text-center text-xl font-semibold uppercase leading-none">
              {formatDeliveryPrice(option.price, option.deliveryRuleId)}
            </p>
          </div>
        ))}
      </div>
      <p className="text-center">
        *{t("text-complete-your-order-by")}:{" "}
        <span className="font-semibold">{orderCompletionTime}</span>
      </p>
      <p className="text-center md:px-24">
        <span className="font-semibold">{t("text-please-note")}</span>:{" "}
        {t("text-additional-items-dispatch-warning")}
      </p>
      <p className="text-center">
        {t.rich("text-delivery-request-bespoke-quote", {
          link: (link: React.ReactNode) => (
            <Link href={ROUTES.BESPOKE} className="cursor-pointer underline" target="_blank">
              {link}
            </Link>
          ),
        })}
      </p>
    </div>
  );
};
